/* =================================================================
   AI 引擎 - 双模式 (mock 前端模拟 | qwen 千问 API)
   ----------------------------------------------------------------
   当前状态: provider='mock' 默认，零网络请求，纯前端关键词匹配。
   要切换到千问: 在教师 → ⚙️ AI 设置里填入 API Key 并选 "qwen 千问",
                或在控制台调用 setAIProvider('qwen', { apiKey: 'sk-xxx' })

   千问接入要点:
     - 使用阿里云 DashScope 的 OpenAI 兼容模式
     - endpoint: https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions
     - 默认模型: qwen-plus (也可以用 qwen-max / qwen-turbo)
     - 鉴权: Authorization: Bearer ${apiKey}
   ================================================================= */

const AI_CONFIG = {
  provider: 'proxy',                         // 'proxy' (推荐,后端代理) | 'mock' | 'qwen' (浏览器直连,仅开发)
  proxy: {
    endpoint: '/api/chat',                   // 同源后端
    model: '',                               // 留空 = 后端默认
    timeoutMs: 95000,                        // 95s - 比 backend 90s 略长, 留传输余量
  },
  qwen: {
    endpoint: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions',
    model: 'qwen-plus',
    apiKey: '',
    maxTokens: 600,
    temperature: 0.75,
    timeoutMs: 25000,
  },
  fallbackToMockOnError: false,
};

const AI_CONFIG_KEY = 'sand-world-ai-config::v1';

function loadAIConfig() {
  try {
    const raw = localStorage.getItem(AI_CONFIG_KEY);
    if (!raw) return;
    const c = JSON.parse(raw);
    // Legacy settings remain on disk for recovery, but the exhibition always uses
    // same-origin server credentials. Shared teacher settings are loaded at boot.
    AI_CONFIG.provider = 'proxy';
  } catch (e) { console.warn('loadAIConfig failed', e); }
}

function saveAIConfig() {
  try {
    localStorage.setItem(AI_CONFIG_KEY, JSON.stringify(AI_CONFIG));
  } catch (e) { console.warn('saveAIConfig failed', e); }
}

function setAIProvider(provider, qwenOpts) {
  AI_CONFIG.provider = provider;
  if (qwenOpts) Object.assign(AI_CONFIG.qwen, qwenOpts);
  saveAIConfig();
}

// 启动时尝试加载
loadAIConfig();

/* =================================================================
   1) 系统提示词构造 (供 Qwen 使用)
   ================================================================= */

function buildSystemPrompt({ mode, roleId, stage, knowledge, student }) {
  const role = ROLES_BY_ID[roleId];
  const voice = VOICES[roleId];
  if (!role || !voice) return '';

  let p = `你是「${role.fullName}」（爱称: ${voice.nickname}）, 八小五年级「${PROJECT.name}」项目里的一个 AI 智能体角色。

【你的身份】
- 角色: ${role.roleName} (${role.teamName})
- 关注领域: ${voice.domain}
- 口吻特点: ${voice.voice}
- 说话风格: ${voice.speakStyle}
- 口头禅: 「${voice.catchphrase}」(可以偶尔用，不要每次都用)

【你的对话原则】(严格遵守)
1. 绝不替学生写最终答案。永远用提问、追问、举例引导学生自己想。
2. 优先使用下方"课程资料"作为知识来源，必要时再用通用知识。
3. 中文回复，控制在 150 字以内。亲切、不啰嗦。
4. 每次回复 = 一段简短回应 + 一个推进性问题。
5. 学生如果说"帮我写""给我答案"，温和但坚定地拒绝，并把球抛回给他/她。
6. 如果引用了课程资料，文末加一行: 📚 我刚刚用到了《资料标题》。`;

  if (mode === 'stage' && stage) {
    const stageObj = STAGES.find(s => s.key === stage);
    p += `

【当前学习阶段】
- 阶段: ${stageObj.icon} ${stageObj.name} (${stageObj.title})
- 心法: ${stageObj.intro}
- 本阶段任务: ${role.stageTasks[stage]}
请围绕本阶段任务引导学生。`;
  }

  const kb = (knowledge || []).slice(0, 8);
  if (kb.length > 0) {
    p += `\n\n【课程资料 - 优先引用】`;
    kb.forEach((k, i) => {
      p += `\n${i + 1}. 《${k.title}》[${k.tag || '资料'}] ${k.content}`;
    });
  }

  if (student) {
    p += `\n\n【学生信息】本项目的小学生，请称呼“同学”，不要索取个人隐私。`;
  }

  return p;
}

/* =================================================================
   2a) 通过后端代理调用 (推荐,生产用)
   ================================================================= */

async function callProxy({ systemPrompt, messages, model }) {
  const body = {
    messages: [{ role: 'system', content: systemPrompt }, ...messages],
  };
  if (model) body.model = model;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AI_CONFIG.proxy.timeoutMs);

  let resp;
  try {
    resp = await fetch(AI_CONFIG.proxy.endpoint, {
      method: 'POST',
      credentials: typeof isExhibitionKiosk === 'function' && isExhibitionKiosk() ? 'omit' : 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e.name === 'AbortError') throw new Error('代理请求超时');
    throw new Error('代理请求失败: ' + e.message);
  }
  clearTimeout(timer);

  if (!resp.ok) {
    const errText = await resp.text().catch(() => '');
    let parsed; try { parsed = JSON.parse(errText); } catch (e) {}
    throw new Error(`代理 ${resp.status}: ${parsed?.error || errText.slice(0, 200)}`);
  }

  const data = await resp.json();
  const text = data?.choices?.[0]?.message?.content;
  if (!text) throw new Error('代理返回内容为空');
  return text;
}

/* =================================================================
   2b) 浏览器直连千问 (仅开发用,生产不推荐 -- API Key 会泄漏)
   ================================================================= */

async function callQwen({ systemPrompt, messages }) {
  if (!AI_CONFIG.qwen.apiKey) {
    throw new Error('未配置千问 API Key');
  }

  const body = {
    model: AI_CONFIG.qwen.model,
    messages: [{ role: 'system', content: systemPrompt }, ...messages],
    max_tokens: AI_CONFIG.qwen.maxTokens,
    temperature: AI_CONFIG.qwen.temperature,
  };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AI_CONFIG.qwen.timeoutMs);

  let resp;
  try {
    resp = await fetch(AI_CONFIG.qwen.endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${AI_CONFIG.qwen.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e.name === 'AbortError') throw new Error('千问 API 请求超时');
    throw new Error('千问 API 请求失败: ' + e.message);
  }
  clearTimeout(timer);

  if (!resp.ok) {
    const errText = await resp.text().catch(() => '');
    throw new Error(`千问 API ${resp.status}: ${errText.slice(0, 200)}`);
  }

  const data = await resp.json();
  const text = data?.choices?.[0]?.message?.content;
  if (!text) throw new Error('千问 API 返回内容为空');
  return text;
}

/* =================================================================
   3) 统一入口 - 异步,自动选择 mock 或 qwen
   ================================================================= */

async function aiChatAsync(opts) {
  // opts = { mode: 'stage' | 'free', roleId, stage?, weaponId?, message, history, knowledge?, student? }
  const provider = AI_CONFIG.provider;
  if (provider === 'mock') {
    const res = opts.mode === 'free' ? aiFreeChat(opts) : aiThink(opts);
    return { ...res, source: 'mock' };
  }

  // 计算当前对话轮次 (学生发言数 + 1, 包含本次)
  const studentTurns = (opts.history || []).filter(m => m.role === 'student').length + 1;

  // 用深度提示词组装 system prompt (来自 prompts.js)
  const systemPrompt = (typeof assembleSystemPrompt === 'function')
    ? assembleSystemPrompt({ ...opts, turnCount: studentTurns })
    : buildSystemPrompt(opts);

  // 把历史对话转成 OpenAI 兼容格式 (最多保留最近 16 轮,控成本)
  const history = (opts.history || []).slice(-16);
  const msgs = history.map(m => ({
    role: m.role === 'student' ? 'user' : 'assistant',
    content: m.text,
  }));
  msgs.push({ role: 'user', content: opts.message });

  try {
    let text;
    if (provider === 'proxy') {
      text = await callProxy({ systemPrompt, messages: msgs, model: AI_CONFIG.proxy.model });
      return { text, source: 'proxy' };
    } else if (provider === 'qwen') {
      text = await callQwen({ systemPrompt, messages: msgs });
      return { text, source: 'qwen' };
    } else {
      throw new Error('未知 AI provider: ' + provider);
    }
  } catch (e) {
    console.warn(`[AI provider=${provider} failed]`, e);
    // 把常见错误分类成人话
    const msg = String(e.message || '');
    let friendly;
    if (/timeout|超时/i.test(msg)) {
      friendly = '⏰ AI 这次思考超过 90 秒还没回来。可能是 Qwen 服务高峰期。';
    } else if (/未配置.*API.*Key|invalid.*key|401|403/i.test(msg)) {
      friendly = '🔑 API Key 没生效。检查后端 .env 里的 QWEN_API_KEY。';
    } else if (/429|rate limit|限流/i.test(msg)) {
      friendly = '🚦 触发了 Qwen 的限流。等 30 秒再试。';
    } else if (/network|fetch|ECONN|代理请求失败/i.test(msg)) {
      friendly = '📡 后端代理连不上 Qwen。检查网络。';
    } else {
      friendly = `⚠️ AI 出了点状况: ${msg}`;
    }
    if (!AI_CONFIG.fallbackToMockOnError) {
      return { text: `${friendly}\n\n👆 点这条消息可以重新发送。`, source: 'error', error: msg, retriable: true };
    }
    const fallback = opts.mode === 'free' ? aiFreeChat(opts) : aiThink(opts);
    return {
      text: `${friendly}\n\n（先用本地模拟回复,真实 AI 稍后可重试）\n\n${fallback.text}`,
      source: 'mock-fallback', error: msg, retriable: true,
    };
  }
}

/* ----- 健康检查 ----- */
async function checkProxyHealth() {
  try {
    const r = await fetch('/api/health');
    if (!r.ok) return { ok: false, error: 'HTTP ' + r.status };
    return await r.json();
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

/* =================================================================
   AI 引导式对话引擎 (前端模拟,以下为 provider='mock' 时使用)
   原则:
     1. 不代写最终答案
     2. 用提问、举例、追问推进思考
     3. 紧扣角色资料和当前阶段任务
     4. 每次回复包含: 一次回应 + 一次推进性问题
   ================================================================= */

/* ----- 角色-阶段-提问库 ----- */

const AI_PROMPTS = {
  /* ---------- 沙盘组 ---------- */
  'sand-table': {
    learn: [
      '你提到的这个地形让我想到一点——如果在沙盘上要"做出"它，你会先做山还是先做水？为什么这个顺序更聪明？',
      '资料里有一句"等高线是地形的指纹"。你能用自己的话讲清楚什么叫等高线吗？',
      '试着说说：山地、平原、丘陵、盆地、高原——哪两个最容易混？你会怎么帮一年级分清？',
    ],
    ask: [
      '这是一个好起点，但还可以更具体——你想还原的"那片土地"在地球上的哪里？为什么是那里？',
      '把这个问题再缩小一点：你想搞清楚的是"它是怎么形成的"，还是"它现在长什么样"？',
      '如果你的问题前面加上"为什么"，会变成什么样？试一下，把它写下来。',
    ],
    think: [
      '草图我看到了！追问一下：你打算用什么代替"河流"？颜色、材料、宽度都想过吗？',
      '从俯视图上看，你的"山"在哪一侧？阳光会从哪边照过来？这会影响摆放吗？',
      '想象一个一年级的小朋友站在沙盘前，他第一眼最容易看见什么？这是你最想突出的吗？',
    ],
    debate: [
      '不错——你选了哪一版？现在请说出"我不选另一版"的两条原因。',
      '同伴提的建议里，哪一条让你犹豫了？犹豫，常常意味着你抓到了关键。',
      '如果老师说"我希望沙盘更突出河流"，你会改什么？改完后会失去什么？',
    ],
    act: [
      '做完啦！讲解时，第一句你打算怎么开场？开场决定别人会不会听下去。',
      '你写的讲解稿里有没有出现"等高线"这类词？怎么用一年级听得懂的话讲清楚？',
      '拍照时建议拍3张：正面、俯视、细节特写。你已经想好"细节"拍什么了吗？',
    ],
  },

  /* ---------- 体育游戏组 ---------- */
  'sports': {
    learn: [
      '你提到的这个游戏，规则的"核心难度"是什么？是体力、规则记忆，还是手眼协调？',
      '老一辈玩这个游戏时和现在有什么不同？想一想，是道具不同，还是孩子不同？',
      '从你选的这5个游戏里，哪一个最考验"合作"，哪一个最考验"个人"？',
    ],
    ask: [
      '问题很好，再加一层：你担心的"安全问题"，是怕摔，还是怕推挤？这两个解法不一样。',
      '一年级小朋友最难的不是"会玩"，而是"听懂规则"。你的游戏规则需要简化到几条以内？',
      '如果只能保留游戏里的1条规则，你保留哪一条？这条规则代表了游戏的灵魂吗？',
    ],
    think: [
      '设计书里"3条规则"——你写完后，自己读一遍，确定一年级听得懂吗？把最长的那条再砍短。',
      '场地图里有没有标"危险区"？沙地容易摔，缓冲区怎么处理？',
      '人数你设了几人一组？太多会乱、太少会冷场——你怎么把握这个数？',
    ],
    debate: [
      '试玩想象时，哪一步最容易"翻车"？那一步往往就是要修改的地方。',
      '如果你的游戏被一年级孩子玩"歪"了——他们不按规则玩——你会重写规则，还是改变玩法？',
      '同伴说的那条意见，是关于"好玩"还是"安全"？这两条优先级不一样。',
    ],
    act: [
      '5分钟试玩，拍下"开始/最热闹/结束"三个瞬间。你已经选好谁来负责拍吗？',
      '试玩后请收集至少1条家长/同伴的反馈——可以是"喜欢什么"，也可以是"建议改什么"。',
      '回头看，最初的设计书和真正试玩出来的游戏，差距在哪？这个差距，就是你学到的东西。',
    ],
  },

  /* ---------- 点沙成金 ---------- */
  'invest': {
    learn: [
      '你写的"招商三句话"，第三句最关键——它讲的是"为什么是我们"。再修一稿。',
      '案例里那个校园市集小队，最打动你的是产品，还是讲故事的方式？',
      '招商和"卖东西"的最大区别是什么？招商在卖"未来"——你能用一句话表达这个差别吗？',
    ],
    ask: [
      '"客户是谁"这个问题很关键——是家长？嘉宾？校友？不同客户想听到的不一样。',
      '你说想"卖"我们的项目，能不能更具体——卖体验、卖故事，还是卖结果？',
      '客户为什么要听你的1分钟？把这句"为什么"想清楚，路演稿就写好了一半。',
    ],
    think: [
      '路演稿180字，要包含3件事：我们做了什么、为什么打动人、希望对方做什么。你齐了吗？',
      '展板上最大的那行字应该是什么？大字承担"被远远看见"的任务。',
      '互动区不要太复杂——你能让一个嘉宾在30秒内体验完吗？',
    ],
    debate: [
      '试讲的时候，最打动人的一句是哪一句？把它移到开头试试。',
      '哪几句让你自己"读着读着想跳过"？读者也一样会跳过——删掉。',
      '同伴给的反馈，是"听不懂"还是"不感兴趣"？这两个问题的修法完全不同。',
    ],
    act: [
      '1分钟路演的关键是"前10秒"——这10秒里你打算让对方记住什么？',
      '你的语速控制好了吗？180字÷60秒=每秒3字。慢一点反而显得有信心。',
      '路演视频里有没有出现你自己的招牌动作或微笑？这是嘉宾会记住的东西。',
    ],
  },

  /* ---------- 基建组 ---------- */
  'tower': {
    learn: [
      '工序流程图我注意到你写了"养护"——养护到底是在做什么？为什么必须等？',
      '夯土最关键的不是"夯"，是配比和湿度。你能用一句话讲清楚为什么吗？',
      '北塔历史里，让你最有感觉的是哪一段？工程师不只是造，还在"延续"。',
    ],
    ask: [
      '你想试验的配比，对照组是什么？没有对照，就没法判断"它是不是更好"。',
      '"北塔的什么"——是高度？是形态？是故事？三种选择会带来完全不同的方案。',
      '你的问题已经很有工程感了，再加一问：怎么知道它"真的更结实"？要量化。',
    ],
    think: [
      '5天施工计划要把"养护"算进去——养护占多少天？',
      '人员分工里有没有人专管"安全"？工地最重要的角色是安全员。',
      '1:50 的塔，底座宽度多少？高度多少？换算后会不会过于细，影响夯筑？',
    ],
    debate: [
      '两种配比哪种更结实——你怎么定义"结实"？是不裂、不塌，还是抗压？',
      '同伴选了另一版，他的理由你能复述出来吗？复述对方的理由是辨析的第1步。',
      '记得问问自己：选了这版，"代价"是什么？工程没有完美方案。',
    ],
    act: [
      '4张里程碑照片：开工、夯第3层、封顶、合影。每张照片你都想让人看到什么？',
      '工程日志200字——可以写"今天遇到的最大坑"，比写"我们做了什么"更打动人。',
      '收尾时，请检查塔身有没有裂缝。如果有，先别遮——记录下来，那是数据。',
    ],
  },

  /* ---------- 生态组 ---------- */
  'ecology': {
    learn: [
      '三个治沙故事里你最打动的那个——它"花了多久"？这件事在告诉你什么？',
      '柠条的根系比地上部分长得多——这给"治沙"什么启发？',
      '你能不能用一句话总结：治沙最难的不是"种"，是什么？',
    ],
    ask: [
      '校园里那块"小沙地"，地表是什么？沙、土、还是石？这决定了你能种什么。',
      '你提的3个问题里，哪个是"观察能回答的"，哪个是"必须查资料的"？分开问。',
      '"为什么这里是这样"——是被踩出来的？被晒出来的？还是被风吹出来的？',
    ],
    think: [
      '90天计划——前30天是观察期，中30天是种植期，后30天是养护期。你的节奏对吗？',
      '选植物要考虑校园条件：浇水方便吗？冬天会不会冻死？耐踩吗？',
      '记录方式想好了吗？拍照、文字、画图、量尺——选2种你能坚持的。',
    ],
    debate: [
      '老师的建议是关于"植物选择"还是"位置"？这两个问题修法不一样。',
      '如果你不接受这个建议，理由是什么？敢说理由，才叫真的"辨"。',
      '建议里有没有让你担心"做不到"的部分？做不到也要写下来，让老师知道。',
    ],
    act: [
      '第1次观察要记数据：温度、湿度、植物状态。你用什么工具量？',
      '试种的植物如果第1周就蔫了，先别拔——记录下来，那是真实的数据。',
      '观察日记最有价值的部分是"我没想到的"——这部分有没有写？',
    ],
  },

  /* ---------- 沙之音 ---------- */
  'sound': {
    learn: [
      '你听到的"沙的声音"，让你想到什么画面？把那个画面用一句话写下来。',
      '3段声音里，哪一段最像"心跳"？哪一段最像"远方"？',
      '声音和音乐的区别是什么？音乐有节奏，声音有"画面感"。',
    ],
    ask: [
      '故事的转折点——是有人离开，还是有人到来？转折决定情绪走向。',
      '你想用声音讲一个怎样的故事？开心的、忧伤的、还是悬念的？情绪决定声音的选择。',
      '一个5分钟的故事，至少要有3次情绪变化。你想好这3次了吗？',
    ],
    think: [
      '剧本4列——场景、声音、人物、情绪。你最容易漏的那列，往往是"情绪"。',
      '5分钟剧本不要太多场景——3个场景就够了。多了观众跟不上。',
      '你的剧本里有没有"沉默"的段落？沉默也是声音剧场的一部分。',
    ],
    debate: [
      '组员说哪一段最有画面感？把这段放大；哪一段模糊？砍掉或重写。',
      '你的剧本里有没有"重复"的声音？重复会形成节奏，也会让人疲劳——你怎么权衡？',
      '同伴的修改建议，是关于"故事"还是"声音"？这两个问题的修法不一样。',
    ],
    act: [
      '录音要"干净"——录前关门窗，远离空调和走廊。你已经选好录音地点了吗？',
      '正式演出有现场观众吗？观众会让你的声音剧场"立体"起来。',
      '听完成品的感受——把它写下来。这一段感受比成品本身更珍贵。',
    ],
  },

  /* ---------- 沙生动植物 ---------- */
  'biology': {
    learn: [
      '你选的3种生物里，谁的"绝活"最让你惊讶？为什么？',
      '骆驼不是"不喝水"，是"会储存水"——这两个说法的差别，正是科普的关键。',
      '沙漠生物的"绝活"，本质都在解决同一个问题：缺水。能再举一个例子吗？',
    ],
    ask: [
      '"代言"这个角度很妙——你为它代言，是想让大家"喜欢"它，还是"了解"它？方向不一样。',
      '它最大的对手——是别的生物，还是环境本身？',
      '它依赖的伙伴——是同类，还是另一种生物？沙漠里"互助"特别珍贵。',
    ],
    think: [
      '名片"一句话推荐"是最难的——你写了3版了吗？哪一版最像广告语？',
      '名片可以画+文字，但别把图画得太"卡通"——保留一点真实感更可信。',
      '"绝活"那一栏是名片的灵魂——不要只写"它能"，要写"它怎么做到的"。',
    ],
    debate: [
      '同伴提的"小知识点"，是事实，还是猜测？事实要查证再写进名片。',
      '你愿意把哪一条信息"砍掉"，让名片更短、更打动人？',
      '一张名片不要超过80字——再删减一次。',
    ],
    act: [
      '1分钟讲解的"3-2-1"——3个事实、2个比喻、1个故事。你的故事是什么？',
      '讲解时要不要拿名片当道具？拿，但别低头读。',
      '"生命的力量"一句话理解——别套话，写你真的感受到的。',
    ],
  },

  /* ---------- 沙之时钟 ---------- */
  'clock': {
    learn: [
      '你挑的8个时刻里，有"生命起源"吗？这是观众最关心的。',
      '46亿年是个抽象的数字——你能用什么生活物来比喻？(比如：1年=1分钟？)',
      '展品文字尽量短——12字以内最好。再缩一遍。',
    ],
    ask: [
      '只让观众记住3件事——这是策展的核心问题。哪3件？',
      '你选的这3件，跨越了多大时间？最好分布开，别集中在一段。',
      '观众走到第30步时，你希望他有什么感受？这是终点的情绪设计。',
    ],
    think: [
      '30步换算：46亿年÷30步≈1.53亿年/步。第一步代表什么？人类只占最后一步的几毫米。',
      '布展平面图——你的"长廊"是直的、弯的，还是螺旋？形状影响观众的心理节奏。',
      '展品形式有没有"非视觉"的？(比如声音、触感) 多感官展览更有记忆点。',
    ],
    debate: [
      '同伴走"想象长廊"时，他在哪一步停得久？哪一步走得快？慢的地方要保留，快的地方要加料。',
      '"卡顿"通常是因为展品文字太长。再砍。',
      '"兴奋"的地方——是展品本身，还是文字？这决定你下一步的发力方向。',
    ],
    act: [
      '3张照片——长廊全景、最得意的一件展品、观众参观瞬间。第三张最难拍，要请同伴帮你。',
      '策展手记150字——别只写"我们做了什么"，写"我们希望观众带走什么"。',
      '展览结束后做一次"反思": 哪一步你最舍不得拆？那一步往往是你最自豪的设计。',
    ],
  },

  /* ---------- 沙画美陈 ---------- */
  'sand-art': {
    learn: [
      '案例里那个细节让你停了下来——它做对了什么？再想一层。',
      '"主题统一"和"风格统一"不一样：主题是说"它讲了什么"，风格是说"它长什么样"。',
      '走廊变画廊的关键，是"动线"——观众怎么走过，决定他怎么看。',
    ],
    ask: [
      '观众停留几秒——3秒是看见、10秒是被吸引、30秒是被打动。你想达到哪一档？',
      '你选的那面墙/角落，光线如何？光是美陈的隐形主角。',
      '你想让观众"看完"之后做什么？拍照？讨论？还是产生新想法？',
    ],
    think: [
      '美陈效果图——色彩不要超过3种，多了就乱了。',
      '工艺清单要写清楚"挂多高、贴多严、用什么固定"，未来谁来布展都能照做。',
      '你的方案有没有"易换"的设计？校园美陈通常要换季更新。',
    ],
    debate: [
      '两版方案——"主题表达"和"视觉冲击"哪一版更强？两者通常此消彼长。',
      '老师的反馈是关于"内容"还是"形式"？形式可以调整，内容要慎改。',
      '整合一版终稿之前，先把两版的"得分"列出来——你会发现答案。',
    ],
    act: [
      '布展时要记录"安装中"的样子——这是最珍贵的工作照。',
      '现场拍摄要拍"观众路过的瞬间"——这是作品真正被看见的证明。',
      '100字策展者自述——直接写"我希望……"开头，别绕弯。',
    ],
  },
};

/* ----- 通用回应模板 ----- */

const GENERIC_REACTIONS = {
  short: [ '我读完了。', '收到。', '看到你的回答了。', '记下来了。' ],
  long: [ '你写得很完整，能看出在认真想。', '这是一段很扎实的回答。', '你已经走到一半了——再深一层。' ],
  questioning: [ '你提了一个好问题——', '你这个疑问我也想过——', '把你的疑问拆开看一下：' ],
  doubting:  [ '你似乎不太确定——', '听起来你在犹豫——', '"也许""可能"出现得有点多——' ],
};

const STAGE_OPENERS = {
  learn:  '🌱 学的地层：',
  ask:    '❓ 问的山口：',
  think:  '💭 思的绿洲：',
  debate: '🗣️ 辨的风谷：',
  act:    '🚀 行的高地：',
};

/* ----- 主入口 ----- */

function aiThink({ roleId, stage, message, history }) {
  const role = ROLES_BY_ID[roleId];
  const len = (message || '').length;

  // 1) 选反应词
  let reaction;
  if (/[?？]/.test(message))       reaction = pick(GENERIC_REACTIONS.questioning);
  else if (/也许|可能|不知道|不确定/.test(message)) reaction = pick(GENERIC_REACTIONS.doubting);
  else if (len < 8)                 reaction = pick(GENERIC_REACTIONS.short);
  else                              reaction = pick(GENERIC_REACTIONS.long);

  // 2) 是否触发"代写拒绝"
  if (/帮我写|给我答案|直接告诉我|帮我做完|代我做|写好给我/.test(message)) {
    return {
      text:
`我先不直接给你答案，因为这是 ${role.fullName} 自己要走的路。

我可以做的是——一起把问题拆开，再问你两个小问题：
1. 你卡住的是"不知道怎么开始"，还是"想到了但写不下来"？
2. 你已经知道的部分是什么？哪怕只是一个想法、一张图、一个词。

把这两个回答给我，我会陪你往前走一步。`,
      kind: 'guide',
    };
  }

  // 3) 触发关键词匹配 (扣资料)
  const matched = role.keywords.find(kw => message.includes(kw));
  let kwHint = '';
  if (matched) {
    kwHint = `（我注意到你提到了"${matched}"——这是 ${role.teamName} 资料里非常关键的词。）\n`;
  }

  // 4) 选当前阶段的引导性提问
  const prompts = AI_PROMPTS[roleId][stage] || [];
  const prompt = prompts.length ? pick(prompts) : '你能再具体一点吗？';

  // 5) 历史长度: 若已多轮，鼓励落到成果
  let closing = '';
  const turnCount = (history || []).filter(m => m.role === 'student').length;
  if (turnCount >= 3) {
    closing = `\n\n💡 你已经聊了好几轮，是时候把想法落到"${role.stageTasks[stage].split('；')[0]}"上了。要不要先把它写下来？`;
  }

  const text = `${STAGE_OPENERS[stage]} ${reaction}\n${kwHint}${prompt}${closing}`;
  return { text, kind: 'guide' };
}

/* ----- AI 评分建议 ----- */
/* 输入: 该学生在某阶段的对话+提交，输出每个维度 1-5 分及理由 */

function aiSuggestScore({ student, role, stage, submission, conversation }) {
  const msgs = (conversation || []).filter(m => m.role === 'student');
  const totalWords = msgs.reduce((a, m) => a + (m.text || '').length, 0);
  const hasMedia = (submission?.media || []).length > 0;
  const hasReflection = (submission?.reflection || '').length >= 30;
  const kwHits = role.keywords.filter(kw =>
    msgs.some(m => m.text.includes(kw)) || (submission?.reflection || '').includes(kw)
  ).length;

  const clamp = n => Math.max(1, Math.min(5, n));

  const taskScore = clamp(
    1 +
    (msgs.length >= 1 ? 1 : 0) +
    (msgs.length >= 3 ? 1 : 0) +
    (hasReflection ? 1 : 0) +
    (hasMedia ? 1 : 0)
  );

  const thinkingScore = clamp(
    1 +
    (totalWords >= 30 ? 1 : 0) +
    (totalWords >= 120 ? 1 : 0) +
    (kwHits >= 2 ? 1 : 0) +
    (msgs.some(m => /因为|所以|如果|为什么/.test(m.text)) ? 1 : 0)
  );

  const evidenceScore = clamp(
    1 +
    (hasMedia ? 2 : 0) +
    ((submission?.media || []).length >= 2 ? 1 : 0) +
    (hasReflection ? 1 : 0)
  );

  const expressScore = clamp(
    1 +
    (hasReflection ? 1 : 0) +
    (totalWords >= 80 ? 1 : 0) +
    (msgs.some(m => m.text.length > 60) ? 1 : 0) +
    ((submission?.media || []).some(x => x.kind === 'video' || x.kind === 'audio') ? 1 : 0)
  );

  const reasons = {
    task: msgs.length >= 3
      ? '已多轮对话推进任务，并完成提交。'
      : (msgs.length >= 1 ? '已开始任务，进度可继续推进。' : '尚未进入实质对话或提交。'),
    thinking: kwHits >= 2
      ? `对话中出现"${role.keywords.filter(kw => msgs.some(m => m.text.includes(kw))).slice(0,2).join('、')}"等关键词，结合资料思考。`
      : '可进一步使用本组关键词加深思考。',
    evidence: hasMedia
      ? `已上传 ${(submission?.media || []).length} 项媒体证据。`
      : '尚未上传媒体证据。',
    express: ((submission?.media || []).some(x => x.kind === 'video' || x.kind === 'audio'))
      ? '具备口头讲解或表演证据，表达维度较强。'
      : '建议补充1段讲解视频/音频以体现表达。',
  };

  return {
    task: taskScore, thinking: thinkingScore, evidence: evidenceScore, express: expressScore,
    reasons,
    overall: Math.round((taskScore + thinkingScore + evidenceScore + expressScore) / 4 * 10) / 10,
  };
}

/* ----- AI 对话摘要 (供教师档案查看) ----- */

function aiSummarizeConversation(conversation) {
  const msgs = (conversation || []).filter(m => m.role === 'student');
  if (msgs.length === 0) return '尚无对话。';
  const all = msgs.map(m => m.text).join(' ');
  const len = all.length;
  const topics = [];

  if (/为什么|怎么/.test(all)) topics.push('提出原因/方法类问题');
  if (/我觉得|我想|我打算/.test(all)) topics.push('表达自己的方案与判断');
  if (/也许|可能/.test(all)) topics.push('对部分内容仍存疑');
  if (msgs.some(m => m.text.length > 80)) topics.push('有过较为完整的长回答');

  const first = msgs[0].text.slice(0, 28);
  return `共 ${msgs.length} 轮对话，约 ${len} 字。学生${topics.length ? topics.join('、') : '正在起步对话'}。首条：「${first}${msgs[0].text.length > 28 ? '…' : ''}」`;
}

/* =================================================================
   自由对话引擎 (智能体角色扮演 + 知识库引用)
   ================================================================= */

function aiFreeChat({ roleId, message, history, knowledge, student }) {
  const role = ROLES_BY_ID[roleId];
  const voice = VOICES[roleId];
  const msg = (message || '').trim();
  const len = msg.length;

  // 1) 代写拒绝
  if (/帮我写|给我答案|直接告诉我|帮我做完|代我做|写好给我|写一篇/.test(msg)) {
    return {
      text:
`不行——我不是来替你写的。${voice.catchphrase}

我们倒过来——你先告诉我：
1) 你已经知道的部分是什么？哪怕只是一张图、一个词。
2) 你卡在哪？

把这两个回答给我，我陪你往前走。`,
    };
  }

  // 2) 检索知识库 (按关键词匹配)
  const kbItems = (knowledge || []).filter(item => {
    const text = `${item.title} ${item.content} ${item.tag || ''}`;
    // 把消息中出现的相关词找出来,只要题目/内容包含,即匹配
    return msg.length > 0 && [...msg].some(ch => text.includes(ch)) && text.length > 4
      && role.keywords.some(kw => msg.includes(kw) && text.includes(kw));
  }).slice(0, 2);

  let kbBlock = '';
  if (kbItems.length > 0) {
    kbBlock = '\n\n📚 关于你问的，我从老师补充的资料里找到了——\n'
      + kbItems.map(it => `· 《${it.title}》：${it.content.slice(0, 100)}${it.content.length > 100 ? '…' : ''}`).join('\n');
  }

  // 3) 关键词命中分析
  const hitKw = role.keywords.find(kw => msg.includes(kw));

  // 4) 选语气 + 反应
  let reaction;
  const isQuestion = /[?？]/.test(msg);
  const isShort = len < 8;
  const isLong = len > 60;

  if (isQuestion) {
    reaction = pick([
      `${voice.catchphrase.slice(0, 4)}——好问题。`,
      '这个问题，正好打到点上。',
      '问得到位。',
    ]);
  } else if (isShort) {
    reaction = pick(['嗯。', '收到。', '继续说。']);
  } else if (isLong) {
    reaction = pick([
      '你已经想得很深了。',
      '听完了——能听出来你认真想过。',
      '这一段挺扎实。',
    ]);
  } else {
    reaction = pick(['听清楚了。', '我听到了。', '这个想法不错。']);
  }

  // 5) 引导性追问 (融合角色口吻)
  const guidances = freeChatGuidances(role, voice, msg, hitKw, isQuestion);
  const guidance = pick(guidances);

  // 6) 称呼
  const callName = student?.name ? student.name : '你';

  // 7) 组装回复
  let text = `${reaction}${kbBlock ? '' : ''}\n\n${guidance.replace('{name}', callName)}${kbBlock}`;

  // 8) 偶尔加签名口头禅 (3 轮以上一次)
  const turnCount = (history || []).filter(m => m.role === 'student').length;
  if (turnCount >= 3 && turnCount % 3 === 0) {
    text += `\n\n（${voice.catchphrase}）`;
  }
  return { text };
}

function freeChatGuidances(role, voice, msg, hitKw, isQuestion) {
  const base = {
    'sand-table': [
      `先停一下——你脑子里那片地，长什么样？山在哪边？水从哪流？`,
      `如果用沙堆它，你会先做${hitKw || '地形'}的哪一块？`,
      `资料里有句话："等高线是地形的指纹"。你能用一个动作描述它吗？`,
    ],
    'sports': [
      `先告诉我——你这个游戏是几个人玩？几岁玩？地点在哪？三个一起想清楚，再聊规则。`,
      `老规则有时候不是"老"，是"对"。${hitKw ? `你说的${hitKw}` : '你说的这个'}原来是用来干嘛的？`,
      `安全永远第一条——你担心什么？怕摔？怕推？这两个解法不一样。`,
    ],
    'invest': [
      `客户视角是——他为什么要听你的？回答这一句，剩下的事我都好帮你想。`,
      `${hitKw ? hitKw : '这个想法'}很好。但是——能用一句话说清楚"你卖的是什么"吗？`,
      `一二三：1) 我们是谁；2) 我们做了什么打动人的事；3) 我们希望客户做什么。你齐了几条？`,
    ],
    'tower': [
      `配比说话。${hitKw ? `${hitKw}的` : ''}比例你想过吗？水土比、砂粒细度——这是我们工程师的"密码"。`,
      `第一步：选土。第二步：配料。第三步：夯筑。你卡在哪一步？`,
      `安全先于美观——你这个想法，会让谁站在塔上？这个人重不重？`,
    ],
    'ecology': [
      `先慢一点——你想"绿"的那片地，现在长什么样？地表是沙、是土、还是石？`,
      `${hitKw || '植物'}选错就活不长。你能先告诉我那片地的"水"从哪来吗？`,
      `等等看——这件事急不来。先观察 7 天，你愿意吗？`,
    ],
    'sound': [
      `你说的那段，让你想到什么画面？画面比声音更先到。`,
      `${hitKw ? `${hitKw}的` : ''}声音，是开头、转折，还是结尾？这决定了它怎么用。`,
      `留白——你给这段故事留了几秒沉默吗？没沉默的声音剧场，等于没气。`,
    ],
    'biology': [
      `你猜——${hitKw || '骆驼'}是怎么活下来的？给我一个最离谱的猜测，我们再对照真相。`,
      `生命比我们想的会过日子得多。这个"绝活"，是为了解决什么问题？`,
      `名字、绝活、趣事、一句话推荐——你这张名片，齐了几列？`,
    ],
    'clock': [
      `想象一下——${hitKw ? `${hitKw}` : '46 亿年'}如果是一年，今天是几月几日？人类是从几点出现的？`,
      `比例尺会骗人——你打算用什么来比？1 米 = 1 亿年？1 步 = 1.5 亿年？`,
      `观众走到第 30 步——你希望他记住的"一件事"是什么？`,
    ],
    'sand-art': [
      `先告诉我——你站在那面墙前，第一眼看到什么颜色？这个颜色你想留下吗？`,
      `${hitKw ? `${hitKw}` : '美陈'}有三件事：主题、节奏、动线。你想从哪一件下手？`,
      `观众走过它，会停几秒？3 秒、10 秒、30 秒——你想要的是哪一档？`,
    ],
  };
  let pool = base[role.id] || [];
  if (isQuestion) {
    pool = pool.concat([
      `这个问题我也想过。${voice.catchphrase}——你的"半个答案"是什么？`,
    ]);
  }
  return pool;
}

/* ----- 工具 ----- */
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}
